import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BookOpen, Star, Calendar, Mail, User, BadgeCheck, Heart, Tag, Users, MessageCircle, Pencil, KeyRound, Upload, BookPlus, Compass, Library, Settings, Camera, Download, Trash2, Image } from "lucide-react";
import { renderDataCard } from "../lib/renderDataCard";
import { t, useLang } from "../lib/i18n";
import { InstallAppButton } from "../components/InstallAppButton";

const NAV_SHORTCUTS = [
  { to: "/discover", key: "nav.discover", icon: Compass },
  { to: "/library", key: "nav.library", icon: Library },
  { to: "/recommendations", key: "nav.need_rec", icon: Star },
  { to: "/lists", key: "nav.lists", icon: Tag },
  { to: "/wishlist", key: "nav.wishlist", icon: Heart },
  { to: "/add", key: "nav.add_book_full", icon: BookPlus },
  { to: "/scan-photo", key: "nav.scan_photo", icon: Camera },
  { to: "/messages", key: "nav.messages", icon: MessageCircle },
  { to: "/sub-accounts", key: "nav.sub_accounts", icon: Users },
  { to: "/subscribe", key: "nav.plus", icon: BadgeCheck },
  { to: "/faq", key: "footer.faq", icon: BookOpen },
];

export default function AccountPage() {
  useLang();
  const { user, refresh } = useAuth();
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [edit, setEdit] = useState(null); // "name" | "email" | "password" | null

  useEffect(() => {
    api.get("/me/library").then(({ data }) => setLibrary(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/me/subscription").then(({ data }) => setSubscription(data)).catch(() => {});
  }, []);

  const totalBooks = library.length;
  const avgRating = totalBooks ? (library.reduce((s, i) => s + i.my_rating, 0) / totalBooks).toFixed(2) : "0.00";
  const fiveStars = library.filter((i) => i.my_rating === 5).length;
  // Reviews written = books that have a non-empty review body.
  const reviewsWritten = library.filter((i) => (i.review || "").trim().length > 0).length;
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="account-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">{t("account.eyebrow")}</div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 mb-10">{t("account.title")}</h1>

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-8">
          <div className="border border-[rgba(0,0,0,0.08)] p-8 space-y-6 bg-[#FAF5E6]">
            <ProfileAvatarBlock user={user} refresh={refresh} />

            <div className="space-y-3 pt-2 border-t border-[rgba(0,0,0,0.08)]">
              <EditableRow icon={<User className="w-3.5 h-3.5" strokeWidth={1.5} />} label={t("account.name")} value={user?.name || ""} onEdit={() => setEdit("name")} testId="row-name" />
              <EditableRow icon={<Mail className="w-3.5 h-3.5" strokeWidth={1.5} />} label={t("account.email")} value={user?.email || ""} onEdit={() => setEdit("email")} testId="row-email" />
              <EditableRow icon={<KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />} label={t("account.password")} value="••••••••••" onEdit={() => setEdit("password")} testId="row-password" />
              <Row icon={<Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />} label={t("account.member_since")} value={memberSince} testId="account-since" />
            </div>
          </div>
        </div>

        <div className="md:col-span-7 space-y-8">
          {subscription && <SubscriptionCard subscription={subscription} />}

          <YourDataCard />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-b border-[rgba(0,0,0,0.08)] py-6">
            <Stat label={t("library.books_read")} value={loading ? "…" : totalBooks} testId="account-stat-books" />
            <Stat label={t("library.avg_rating")} value={loading ? "…" : avgRating} testId="account-stat-avg" />
            <Stat label={t("library.five_star")} value={loading ? "…" : fiveStars} testId="account-stat-five" />
            <Stat label={t("home.reviews_written")} value={loading ? "…" : reviewsWritten} testId="account-stat-reviews" />
          </div>

          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-3 inline-flex items-center gap-1.5">
              <Settings className="w-3 h-3" strokeWidth={1.5} /> {t("account.quick_nav")}
            </div>
            <div className="grid grid-cols-2 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)]">
              {NAV_SHORTCUTS.map(({ to, key, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  className="bg-[#FAF5E6] p-4 hover:bg-[#F2EAD3] transition-colors flex items-center gap-3"
                  data-testid={`nav-${to.replace(/\W/g, "-")}`}
                >
                  <Icon className="w-4 h-4 text-[#0A0A0A]" strokeWidth={1.5} />
                  <span className="font-heading text-sm text-[#0A0A0A]">{t(key)}</span>
                </Link>
              ))}
            </div>
            <div className="mt-6">
              <InstallAppButton />
            </div>
          </div>
        </div>
      </div>

      <EditProfileModal kind={edit} onClose={() => setEdit(null)} onSaved={async () => { await refresh?.(); setEdit(null); }} user={user} />
    </div>
  );
}

function ProfileAvatarBlock({ user, refresh }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("account.pick_image")); return; }
    setBusy(true);
    try {
      const dataUrl = await shrinkToDataUrl(file, 256, 0.82);
      await api.patch("/me", { avatar_url: dataUrl });
      await refresh?.();
      toast.success(t("account.pic_updated"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="relative w-20 h-20 rounded-full bg-[#E8DFC1] border border-[rgba(0,0,0,0.08)] flex items-center justify-center font-heading text-3xl text-[#0A0A0A] overflow-hidden group"
        data-testid="account-avatar"
        title={t("account.change_pic")}
      >
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
        ) : (
          (user?.name?.[0] || "?").toUpperCase()
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Upload className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
      <div>
        <div className="font-heading text-2xl text-[#0A0A0A] leading-tight" data-testid="account-name">{user?.name}</div>
        <div className="font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3] mt-1">{user?.role}</div>
        {busy && <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-1">{t("account.uploading")}</div>}
      </div>
    </div>
  );
}

async function shrinkToDataUrl(file, maxDim = 256, quality = 0.82) {
  // Browser-side resize so we don't bloat MongoDB. Returns a data: URL.
  const blobUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = blobUrl;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

function SubscriptionCard({ subscription }) {
  const alreadyCancelling = subscription.auto_renew === false || subscription.cancel_requested;

  return (
    <div className={`p-6 border ${subscription.is_plus ? "border-[#0A0A0A]/40 bg-[#E8DFC1]/40" : "border-[rgba(0,0,0,0.10)] bg-[#FAF5E6]"}`} data-testid="subscription-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#0A0A0A] inline-flex items-center gap-1.5">
            <BadgeCheck className="w-3 h-3" strokeWidth={1.5} /> {subscription.is_plus ? "Reads+" : t("account.free_label")}
          </div>
          <div className="font-heading text-2xl text-[#0A0A0A] mt-1 tracking-tight">
            {subscription.is_plus ? t("account.unlimited") : t("account.free_plan")}
          </div>
          {subscription.is_plus && subscription.expires_at && (
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mt-1" data-testid="subscription-expires">
              {alreadyCancelling ? t("account.ends") : t("account.renews_expires")} {new Date(subscription.expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
        {!subscription.is_plus && (
          <Link to="/subscribe" className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors" data-testid="upgrade-link">
            {t("account.upgrade")}
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)] mt-6">
        <UsageBar label={t("account.books_added")} used={subscription.limits.books_used} limit={subscription.limits.books_limit} />
        <UsageBar label={t("nav.recommendations")} used={subscription.limits.recs_used} limit={subscription.limits.recs_limit} />
      </div>
      {subscription.is_plus && !subscription.lifetime && (
        <div className="mt-6 pt-5 border-t border-[rgba(0,0,0,0.08)]">
          {alreadyCancelling ? (
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]" data-testid="subscription-cancel-pending">
              {t("account.autorenew_off")}
            </div>
          ) : (
            // Route through /cancel-plan so we can collect a cancel reason
            // (fed back to the team inbox). Cancel logic lives in one page.
            <Link
              to="/cancel-plan"
              className="font-mono uppercase tracking-widest text-[10px] text-[#8A0F0F] hover:text-[#5C0808] underline underline-offset-4"
              data-testid="cancel-plus-button"
            >
              {t("account.cancel_plus")}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function EditProfileModal({ kind, onClose, onSaved }) {
  useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!kind) {
      setName(""); setEmail(""); setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    }
  }, [kind]);

  if (!kind) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {};
      if (kind === "name") payload.name = name.trim();
      if (kind === "email") { payload.email = email.trim(); payload.current_password = currentPassword; }
      if (kind === "password") {
        if (newPassword !== confirmPassword) { toast.error(t("account.pw_mismatch")); setSubmitting(false); return; }
        payload.current_password = currentPassword;
        payload.new_password = newPassword;
      }
      await api.patch("/me", payload);
      toast.success(`${t("account.updated_toast")} ${kind}`);
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="edit-profile-modal">
      <form onSubmit={submit} className="bg-[#F2EAD3] max-w-md w-full p-8 border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]" onClick={(e) => e.stopPropagation()}>
        <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2">{t("account.edit_profile")}</div>
        <h3 className="font-heading text-2xl text-[#0A0A0A] mb-6 tracking-tight">
          {kind === "name" && t("account.change_name")}
          {kind === "email" && t("account.change_email")}
          {kind === "password" && t("account.change_password")}
        </h3>

        {kind === "name" && (
          <Field label={t("account.new_name")}>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="input" data-testid="edit-name-input" autoFocus />
          </Field>
        )}

        {kind === "email" && (
          <>
            <Field label={t("account.new_email")}>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" data-testid="edit-email-input" autoFocus />
            </Field>
            <Field label={t("account.current_pw_confirm")}>
              <input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="input" data-testid="edit-email-confirm-pw" />
            </Field>
          </>
        )}

        {kind === "password" && (
          <>
            <Field label={t("account.current_pw")}>
              <input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="input" data-testid="edit-pw-current" autoFocus />
            </Field>
            <Field label={t("account.new_pw")}>
              <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input" data-testid="edit-pw-new" />
            </Field>
            <Field label={t("account.confirm_new_pw")}>
              <input type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input" data-testid="edit-pw-confirm" />
            </Field>
          </>
        )}

        <div className="flex items-center gap-3 mt-6">
          <button type="submit" disabled={submitting} className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50" data-testid="edit-submit">
            {submitting ? t("account.saving") : t("common.save")}
          </button>
          <button type="button" onClick={onClose} className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A]" data-testid="edit-cancel">{t("common.cancel")}</button>
        </div>

        <style>{`.input { width:100%; background:#FAF5E6; border:1px solid rgba(10,10,10,0.12); border-radius:0.125rem; padding:0.6rem 0.75rem; font-size:0.875rem; outline:none; }
          .input:focus { border-color:#0A0A0A; box-shadow:0 0 0 1px #0A0A0A; }`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function EditableRow({ icon, label, value, onEdit, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] text-[#525252]">{icon}{label}</span>
      <span className="flex items-center gap-2 min-w-0">
        <span className="text-[#0A0A0A] truncate" data-testid={testId}>{value}</span>
        <button type="button" onClick={onEdit} className="text-[#4A4030] hover:text-[#0A0A0A]" data-testid={`${testId}-edit`}>
          <Pencil className="w-3 h-3" strokeWidth={1.5} />
        </button>
      </span>
    </div>
  );
}

function Row({ icon, label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] text-[#525252]">{icon}{label}</span>
      <span className="text-[#0A0A0A] truncate" data-testid={testId}>{value}</span>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div>
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{label}</div>
      <div className="font-heading text-3xl text-[#0A0A0A] mt-1" data-testid={testId}>{value}</div>
    </div>
  );
}

function UsageBar({ label, used, limit }) {
  const isUnlimited = limit == null;
  const pct = isUnlimited ? 100 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  return (
    <div className="bg-[#FAF5E6] p-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{label}</span>
        <span className="font-mono text-[11px] text-[#0A0A0A] tabular-nums">{isUnlimited ? t("account.unlimited_word") : `${used} / ${limit}`}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#E8DFC1] border border-[rgba(0,0,0,0.06)] overflow-hidden">
        <div className="h-full bg-[#0A0A0A]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * GDPR self-service card. Fetches `/me/data` on demand, renders a summary
 * of everything the user has stored with Reads, offers a JSON download,
 * and provides a nuke button (`DELETE /me`) that wipes the account.
 */
function YourDataCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const loadSummary = async () => {
    if (loading || summary) return;
    setLoading(true);
    try {
      const { data } = await api.get("/me/data");
      setPayload(data);
      setSummary(data.summary || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reads-data-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Data downloaded");
  };

  const downloadCard = async () => {
    if (!payload) return;
    try {
      const pngUrl = await renderDataCard(payload);
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = `reads-data-card-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Couldn't render the card — try JSON instead");
    }
  };

  const nuke = async () => {
    const confirmed = window.confirm(
      "Delete your account permanently?\n\nThis wipes your books, reviews, wishlist, lists, and payment history. It cannot be undone."
    );
    if (!confirmed) return;
    const typed = window.prompt("Type DELETE (in caps) to confirm.");
    if (typed !== "DELETE") return;
    setDeleting(true);
    try {
      await api.delete("/me");
      toast.success("Account deleted");
      await logout?.();
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const LABELS = {
    account: "Account profile",
    books_added: "Books you've added",
    reviews: "Reviews you've written",
    wishlist: "Wishlist items",
    lists_sections: "Custom lists / sections",
    author_follows: "Authors you follow",
    author_reviews: "Author reviews",
    payment_transactions: "Payment records",
    email_log: "Emails we've sent you",
  };

  return (
    <div className="bg-[#FAF5E6] border border-[rgba(10,10,10,0.10)] p-6 md:p-8" data-testid="your-data-card">
      <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#4A4030] mb-2">Your data</div>
      <h3 className="font-heading text-xl text-[#0A0A0A] tracking-tight mb-2">Everything we&apos;ve collected about you</h3>
      <p className="text-sm text-[#4A4030] leading-relaxed mb-5">
        Under GDPR you have the right to see all data we hold, export it, and ask us to delete it. Everything happens on this page — no email back-and-forth.
      </p>

      {!summary ? (
        <button
          type="button"
          onClick={loadSummary}
          disabled={loading}
          className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-4 py-2.5 rounded-sm transition-colors disabled:opacity-50"
          data-testid="load-my-data-btn"
        >
          {loading ? "Loading…" : "Show my data"}
        </button>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)] mb-5" data-testid="my-data-summary">
            {Object.entries(summary).map(([key, count]) => (
              <div key={key} className="bg-[#F2EAD3] p-3">
                <div className="font-mono uppercase tracking-widest text-[9px] text-[#525252]">{LABELS[key] || key}</div>
                <div className="font-heading text-2xl text-[#0A0A0A] tabular-nums mt-1">{count}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={downloadCard}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-4 py-2.5 rounded-sm transition-colors"
              data-testid="download-data-card-btn"
            >
              <Image className="w-3 h-3" strokeWidth={1.5} /> Download data card
            </button>
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#F2EAD3] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors"
              data-testid="download-my-data-btn"
              title="Full machine-readable export (GDPR portability)"
            >
              <Download className="w-3 h-3" strokeWidth={1.5} /> Raw JSON
            </button>
            <button
              type="button"
              onClick={nuke}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 border border-[#B91C1C]/40 text-[#B91C1C] hover:bg-[#B91C1C]/10 font-mono uppercase tracking-widest text-[10px] px-4 py-2 rounded-sm transition-colors disabled:opacity-50 ml-auto"
              data-testid="delete-my-account-btn"
            >
              <Trash2 className="w-3 h-3" strokeWidth={1.5} /> {deleting ? "Deleting…" : "Delete my account"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

